import React from "react";

/**
 * ProgressRingLight — donut on light backgrounds. Uses Pearl green over a
 * neutral track. Mirrors mobile ProgressRingLight component.
 *
 * When `onActivate` is provided the ring becomes an interactive button:
 * pointer cursor, subtle hover lift, keyboard-focusable, Enter/Space
 * activation, with `ariaLabel` for accessibility. Click events are
 * stopped so parent link/card handlers do not fire.
 */
export default function ProgressRingLight({
    size = 72, strokeWidth = 8, progress = 0,
    ringColor = "#3E9E2E", trackColor = "#E5E7EB",
    showValue = true, testid,
    onActivate, ariaLabel,
}) {
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const clamped = Math.max(0, Math.min(100, progress));
    const dash = (clamped / 100) * circ;

    const svg = (
        <>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={strokeWidth} fill="none" />
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    stroke={ringColor} strokeWidth={strokeWidth}
                    strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" fill="none"
                    style={{ transition: "stroke-dasharray 0.6s ease" }}
                />
            </svg>
            {showValue && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[#131B2F] text-base font-medium">{clamped}%</span>
                </div>
            )}
        </>
    );

    if (!onActivate) {
        return (
            <div
                className="relative inline-flex items-center justify-center"
                style={{ width: size, height: size }}
                data-testid={testid}
            >
                {svg}
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onActivate(e); }}
            aria-label={ariaLabel || `View journey — ${clamped}% complete`}
            data-testid={testid}
            className="relative inline-flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:scale-[1.06] hover:drop-shadow-[0_2px_8px_rgba(62,158,46,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3E9E2E] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            style={{ width: size, height: size }}
        >
            {svg}
        </button>
    );
}
